import { parseMeshSpec, meshWeight } from './mesh.js';
import { computeRectangularTieLength, computeCircleTieLength, computeTripleTieCrossbarLength, computeInscribedDiamondLength, } from './tie-geometry.js';
import { FtIn } from './ft-in.js';
/**
 * Compute the weight (lbs) for a single Line.
 * Dispatches on function code to the appropriate formula.
 * Returns 0 for non-rebar material (GI, MT) or missing size.
 *
 * `defaults` is optional; when supplied, tie weight calculations honor
 * `defaults.seismicHooks` to choose 135° seismic hooks vs 90° standard.
 * Pre-existing callers that don't pass defaults default to 90° standard,
 * preserving prior behavior.
 */
export function computeLineWeight(line, barMarks, defaults) {
    if (line.material !== 'TR')
        return 0;
    if (!line.size) {
        return computeMeshLineWeight(line);
    }
    switch (line.function) {
        case 'BF':
            return line.class === 'S'
                ? computeStraightWeight(line)
                : computeBentWeight(line, barMarks);
        case 'RA':
            return computeAreaRebarWeight(line);
        case 'TC':
        case 'TA':
        case 'TB':
        case 'TD':
            return computeTieWeight(line, defaults);
        case 'SPX':
            return computeSpiralWeight(line);
        default:
            return 0;
    }
}
/**
 * Straight bar: quantity × multiplier × L1 (feet) × weightPerFoot.
 */
function computeStraightWeight(line) {
    if (!line.L1 || !line.size)
        return 0;
    return line.quantity * line.multiplier * line.L1.toDecimalFeet() * line.size.weightPerFoot;
}
/**
 * Bent bars (BF with class L or H): uses theoretical length from the referenced bar mark.
 */
function computeBentWeight(line, barMarks) {
    if (!line.size || !line.barMark)
        return 0;
    const mark = barMarks.get(line.barMark);
    if (!mark)
        return 0;
    return line.quantity * line.multiplier * mark.theoreticalLength.toDecimalFeet() * line.size.weightPerFoot;
}
/**
 * Mesh: parses spec from notes field, multiplies per-unit weight by quantity.
 */
function computeMeshLineWeight(line) {
    if (!line.notes)
        return 0;
    try {
        const spec = parseMeshSpec(line.notes);
        const perUnit = meshWeight(spec);
        return perUnit * Math.max(line.quantity, 1) * line.multiplier;
    }
    catch {
        return 0;
    }
}
/**
 * Area rebar: computes bar count from span/spacing, then weight from bar length.
 * If quantity > 0, it overrides the computed bar count.
 */
function computeAreaRebarWeight(line) {
    if (!line.L1 || !line.size)
        return 0;
    let barCount;
    if (line.quantity > 0) {
        barCount = line.quantity;
    }
    else if (line.span && line.spacing && !line.spacing.isZero()) {
        barCount = Math.floor(line.span.toDecimalInches() / line.spacing.toDecimalInches()) + 1;
    }
    else {
        return 0;
    }
    return barCount * line.multiplier * line.L1.toDecimalFeet() * line.size.weightPerFoot;
}
/**
 * Ties (TA / TB / TC): per-tie bar length × count × multiplier × weightPerFoot.
 *
 * Count: `quantity` if > 0, else `floor(span / spacing) + 1`.
 *
 * Per-tie length:
 *   - TA / TB with L1+L2 populated: rectangular tie formula via
 *     `computeRectangularTieLength` — `2 × (L1 + L2) + 2 × hookExtension`.
 *     Hook = 90° standard. (135° seismic + TB diagonal addition deferred.)
 *   - TC with L1 populated and lap (L5) populated: circular tie formula via
 *     `computeCircleTieLength` — `π × (L1 − 2 × L2 − barDia) + lap`. L2 is
 *     concrete clearance (treated as 0 if null). L5 is the lap length.
 *   - Otherwise: legacy convention where L1 IS the total tie bar length.
 *     Preserved for backward compat with fixtures authored before
 *     per-function geometry landed.
 */
function computeTieWeight(line, defaults) {
    if (!line.L1 || !line.size)
        return 0;
    // aSa convention (per the Estimating Quick Ref Guide and Josh's confirmation
    // 2026-04-27): the estimator may use EITHER `quantity` directly OR the
    // span/spacing pair; both are valid input forms. The L5 column carries the
    // "Extra Ties" count, an additive bump applied AFTER the spacing-derived
    // count. Extras are added in either path.
    const extras = readExtraTies(line);
    let count;
    if (line.quantity > 0) {
        count = line.quantity + extras;
    }
    else if (line.span && line.spacing && !line.spacing.isZero()) {
        // Span/Spacing-derived count: floor(span / spacing). The previously used
        // `+ 1` fence-post add was incorrect — reconciliation against the
        // E26-1660 TB sample (25-00 / 1-00 + 4 extras = 29) confirms no +1.
        count = Math.floor(line.span.toDecimalInches() / line.spacing.toDecimalInches()) + extras;
    }
    else {
        return 0;
    }
    const perTieLengthFt = computePerTieLengthFt(line, defaults);
    return count * line.multiplier * perTieLengthFt * line.size.weightPerFoot;
}
/**
 * Read the Extra Ties count from L5. The aSa data-entry form for tie functions
 * stores Extra Ties as a count integer; the PDF report renders it in the L5
 * column as a bare integer (e.g. "4"). The engine model types L5 as `FtIn`
 * so we interpret L5's whole-inches value as the count.
 *
 * Returns 0 when L5 is null or zero.
 */
function readExtraTies(line) {
    if (!line.L5)
        return 0;
    if (line.function !== 'TA' && line.function !== 'TB' && line.function !== 'TC' && line.function !== 'TD')
        return 0;
    // For TC, L5 is the Lap dimension (an FtIn length), not a count. Skip.
    if (line.function === 'TC')
        return 0;
    return Math.max(0, Math.round(line.L5.toDecimalInches()));
}
function computePerTieLengthFt(line, defaults) {
    // L1 is guaranteed non-null by caller
    if (!line.L1 || !line.size)
        return 0;
    // For rectangular ties: 135° seismic hooks when defaults.seismicHooks is true;
    // 90° standard otherwise. TC circular ties use lap closure (no hook variant
    // applies) — the seismic flag affects rectangular ties only.
    const hookType = defaults?.seismicHooks ? 'seismic_135' : 'standard_90';
    if ((line.function === 'TA' || line.function === 'TB' || line.function === 'TD') &&
        line.L2 !== null) {
        const perimeterFt = computeRectangularTieLength(line.L1, line.L2, line.size, hookType).toDecimalFeet();
        if (line.function === 'TB') {
            // TB Double Tie: rectangle + inscribed diamond per the aSa "Square and
            // diamond tie pattern" (per-set length).
            const diamondFt = computeInscribedDiamondLength(line.L1, line.L2, line.size, hookType).toDecimalFeet();
            return perimeterFt + diamondFt;
        }
        if (line.function === 'TD') {
            // L3 = Clearance per the aSa Triple Tie data-entry form.
            const clearance = line.L3 ?? FtIn.fromDecimalInches(0);
            const crossbarFt = computeTripleTieCrossbarLength(line.L1, clearance, line.size, hookType).toDecimalFeet();
            return perimeterFt + crossbarFt;
        }
        return perimeterFt;
    }
    if (line.function === 'TC' && line.L5 !== null) {
        // L1 = Diameter (concrete), L2 = Clearance (default 0), L5 = Lap.
        return computeCircleTieLength({
            columnDiameter: line.L1,
            cover: line.L2 ?? FtIn.fromDecimalInches(0),
            tieBarSize: line.size,
            closureType: 'lap',
            lap: line.L5,
        }).toDecimalFeet();
    }
    // Legacy: L1 is the total tie bar length
    return line.L1.toDecimalFeet();
}
/**
 * Spirals (SPX): continuous spiral reinforcement.
 * turns = span (height) / spacing (pitch), circumference = π × L1 (diameter).
 * weight = turns × circumference (feet) × weightPerFoot × multiplier.
 */
function computeSpiralWeight(line) {
    if (!line.L1 || !line.size || !line.span || !line.spacing || line.spacing.isZero())
        return 0;
    const circumference = Math.PI * line.L1.toDecimalFeet();
    const turns = line.span.toDecimalInches() / line.spacing.toDecimalInches();
    const totalLength = turns * circumference;
    return totalLength * line.size.weightPerFoot * line.multiplier;
}
